
import { computed, defineComponent, getCurrentInstance, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import config from '@root/config'
import { useBillingStore } from '@/store/stores/billing'
import { useProject } from '@/hooks/useProject'
import { useUser } from '@/hooks/useUser'
import api from '@/api/api'
import InvoiceActivatorSelect from '@/components/inputs/InvoiceActivatorSelect.vue'
import InvoiceModal from '@/modules/marketplace/components/IntegrationInstallModal/InvoiceModal.vue'
import SpinnerLoader from '@/components/SpinnerLoader.vue'
import DetachChannelModal from '@/modules/marketplace/components/billing/DetachChannelModal.vue'
import DowngradeTariffModal from '@/modules/marketplace/components/billing/DowngradeTariffModal.vue'
import ScheduledChangeBlockedModal from '@/modules/marketplace/components/billing/ScheduledChangeBlockedModal.vue'
import Opz2KpiSettingsRequestModal from '@/modules/marketplace/components/billing/Opz2KpiSettingsRequestModal.vue'
import ActivePromocodeNote from '@/modules/marketplace/components/billing/ActivePromocodeNote.vue'
import { useMarketplaceStore } from '@/store/stores/marketplace'
import { describeActivation, describeActivePromocode } from './activePromocode'
import { formatOrderPromocodes, orderPromocodeCodes } from '@/helpers/billing/promocodeDiscountLabel'
import { buildInvoiceItemsFromPaymentContext, isUnassignedChannelSlotName } from './invoiceItems'
import { resolveAddChannelInvoicePeriod, resolveBillingInvoicePeriod } from './invoicePeriod'
import {
    comparableSubscriptionPeriodMonths,
    getSubscriptionPeriodDiscountLabel,
    getSubscriptionPeriodPillItems,
    getPeriodDiscountPercent,
    resolveSubscriptionPeriod,
} from '../../subscriptionPeriodDiscount'
import { useTariffTrialRestrictions, TRIAL_DEFAULT_PERIOD } from '@/hooks/useTariffTrialRestrictions'
import { hasScheduledTariffChange } from '@/helpers/billing/hasScheduledTariffChange'
import { BEAUTY_AUTOPILOT_PLAN_INTEGRATION_IDS } from '@/helpers/beautyAutopilot'

export default defineComponent({
    name: 'PaymentSidebar',
    components: {
        InvoiceActivatorSelect,
        InvoiceModal,
        SpinnerLoader,
        DetachChannelModal,
        DowngradeTariffModal,
        ScheduledChangeBlockedModal,
        Opz2KpiSettingsRequestModal,
        ActivePromocodeNote,
    },
    props: {
        renewal: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: 'billing',
        },
    },
    setup(props) {
        const root = getCurrentInstance()?.proxy
        const billingStore = useBillingStore()
        const { project } = useProject()
        const { user } = useUser()
        const { isTrialRestricted } = useTariffTrialRestrictions()
        // Скрытие периода «на месяц» действует только в основном сценарии оплаты тарифа (не для доп. каналов).
        const periodRestrictionActive = computed(
            () => isTrialRestricted.value && props.mode !== 'add-channel'
        )
        const periodResolveOptions = computed(() => ({
            excludeMonthly: periodRestrictionActive.value,
            preferredFallback: periodRestrictionActive.value ? TRIAL_DEFAULT_PERIOD : '12',
        }))

        const modalOpened = computed(() => billingStore.paymentModal.opened)
        const showTariffPicker = computed(
            () =>
                Boolean(billingStore.paymentModal.showTariffPicker) &&
                props.mode !== 'add-channel'
        )
        const shouldRenderSidebar = computed(() => {
            if (props.mode === 'add-channel') return true
            return Boolean(billingStore.selectedTariffId)
        })
        const renewal = computed(() => {
            if (!showTariffPicker.value) return props.renewal
            const currentId = billingStore.getCurrentTariff(project.value?.id)?.integrationId
            const selectedId = billingStore.selectedTariffId
            if (!currentId || !selectedId) return props.renewal
            return selectedId === currentId
        })
        const renewalCopy = computed(() => {
            if (renewal.value) return true
            if (props.mode === 'add-channel') return false
            const projectId = project.value?.id
            if (!projectId) return false
            const current = billingStore.getCurrentTariff(projectId)
            const previous = billingStore.getPreviousTariff(projectId)
            return !current && Boolean(previous?.integrationId)
        })
        const revealShorterPeriods = ref(false)
        const extraRevealedPeriodValues = ref<string[]>([])
        const isRevealingShorterPeriods = ref(false)
        const splitAnimActive = ref(false)
        const periodRowRef = ref<HTMLElement | null>(null)
        const periodStageRef = ref<HTMLElement | null>(null)
        const setPeriodRowRef = (el: Element | { $el?: unknown } | null) => {
            const node = el && !(el instanceof Element) ? el.$el : el
            periodRowRef.value = node instanceof HTMLElement ? node : null
        }
        const setPeriodStageRef = (el: Element | { $el?: unknown } | null) => {
            const node = el && !(el instanceof Element) ? el.$el : el
            periodStageRef.value = node instanceof HTMLElement ? node : null
        }
        const splitClones = ref<
            {
                id: string
                label: string
                discount: string | null
                active: boolean
                style: Record<string, string>
            }[]
        >([])
        let revealShorterGeneration = 0
        const PERIOD_GAP = 8
        const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
        const waitNextFrame = () =>
            new Promise<void>(resolve => {
                requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
            })
        let periodAnimLayout: { rowWidth: number; height: number } | null = null
        const unlockPeriodStageHeight = () => {
            const stage = periodStageRef.value
            if (stage) stage.style.height = ''
        }
        const lockPeriodStageHeight = (height: number) => {
            const stage = periodStageRef.value
            if (stage) stage.style.height = `${height}px`
        }
        const resetShorterPeriodReveal = () => {
            revealShorterGeneration += 1
            revealShorterPeriods.value = false
            extraRevealedPeriodValues.value = []
            isRevealingShorterPeriods.value = false
            splitAnimActive.value = false
            splitClones.value = []
            periodAnimLayout = null
            unlockPeriodStageHeight()
        }
        const closeModal = () => {
            billingStore.setPaymentModalOpened(false)
            if (props.mode === 'add-channel') {
                billingStore.setAddChannelContext(null)
            }
        }

        const getFormatedPeriodLabel = (n: number): string => {
            switch (n) {
                case 1:
                    return 'на месяц'
                case 3:
                    return 'на 3 месяца'
                case 6:
                    return 'на 6 месяцев'
                case 12:
                    return 'на 12 месяцев'
                case 24:
                    return 'на 24 месяца'
                default:
                    return ''
            }
        }
        const headerTitle = computed(() => {
            const base = renewalCopy.value ? 'Продление подписки' : 'Смена тарифа'
            const periodKey = billingStore.selectedSubscriptionPeriod ?? '1'
            const periodNum = Number(periodKey)
            const periodLabel = getFormatedPeriodLabel(periodNum)
            return periodLabel ? `${base} (${periodLabel})` : base
        })
        const addChannelHeaderTitle = computed(() => {
            const displayName = String(billingStore.addChannelContext?.name || '').trim()
            const kpiSettingsId = config.marketplaceModulesIds[config.buildType]?.opz2KPISettings
            const zvonoBotSettingsId = config.marketplaceModulesIds[config.buildType]?.zvonoBotSettings
            const addChannelIntegrationId = String(billingStore.addChannelContext?.integrationId || '')
            if (kpiSettingsId && addChannelIntegrationId === String(kpiSettingsId)) {
                return renewal.value ? 'Продление настройки KPI' : 'Покупка настройки KPI'
            }
            if (zvonoBotSettingsId && addChannelIntegrationId === String(zvonoBotSettingsId)) {
                return renewal.value
                    ? 'Продление настройки Аудиорассылок DH'
                    : 'Покупка настройки Аудиорассылок DH'
            }
            const channelName = displayName.toLowerCase()
            const isMultiChat = channelName.includes('мультичат')
            const isCustomCascade = channelName.includes('каскад')
            const isReport20 = channelName.includes('отчёт 2.0')
            const isOpz2 = channelName.includes('опз 2.0')
            const isHotWindows = channelName.includes('горячие окна')

            if (isMultiChat) {
                return renewal.value
                    ? 'Продление подписки на модуль Мультичата'
                    : 'Покупка подписки на модуль Мультичата'
            }

            if (isCustomCascade) {
                return renewal.value ? 'Продление Дополнительных каскадов' : 'Покупка Дополнительных каскадов'
            }

            if (isReport20) {
                return renewal.value ? 'Продление подписки Отчёта 2.0' : 'Покупка подписки Отчёта 2.0'
            }

            if (isOpz2) {
                return renewal.value ? 'Продление подписки на модуль ОПЗ 2.0' : 'Покупка подписки на модуль ОПЗ 2.0'
            }

            if (isHotWindows) {
                return renewal.value ? 'Продление подписки на модуль Горячих окон' : 'Покупка подписки на модуль Горячих окон'
            }

            if (
                isModuleItem({
                    integrationId: addChannelContext.value?.integrationId,
                    name: displayName,
                    kind: addChannelContext.value?.itemKind,
                })
            ) {
                const moduleTitle = displayName ? `модуль ${displayName}` : 'модуль'
                return renewal.value
                    ? `Продление подписки на ${moduleTitle}`
                    : `Покупка подписки на ${moduleTitle}`
            }

            return renewal.value ? 'Продление дополнительного канала' : 'Покупка дополнительного канала'
        })

        const currentTariffData = computed(() => billingStore.getCurrentTariff(project.value?.id))
        const selectedTariff = computed(() =>
            billingStore.selectedTariffId
                ? billingStore.getTariffById(billingStore.selectedTariffId)
                : undefined
        )
        const TARIFF_PICKER_ORDER: Record<string, number> = {
            tariff1: 1,
            tariff2: 2,
            tariff3: 3,
        }
        const getTariffLogicalId = (
            tariff?: { id?: string; baseTariffId?: string | null; integrationId?: string } | null,
            fallback = ''
        ) => String(tariff?.baseTariffId || tariff?.id || fallback)
        const TARIFF_CHANNEL_SLOTS: Record<string, number> = {
            tariff1: 1,
            tariff2: 3,
            tariff3: 3,
        }
        const getTariffChannelSlotCount = (
            tariff?: {
                extra?: { channelSlots?: unknown } | null
                id?: string
                baseTariffId?: string | null
                integrationId?: string
            } | null
        ) => {
            const extraSlots = Number(tariff?.extra?.channelSlots)
            if (Number.isFinite(extraSlots) && extraSlots > 0) return extraSlots
            const fromStore = billingStore.getTariffById(
                String(tariff?.id || tariff?.integrationId || '')
            )
            const storeSlots = Number(fromStore?.extra?.channelSlots)
            if (Number.isFinite(storeSlots) && storeSlots > 0) return storeSlots
            const logicalId = getTariffLogicalId(tariff || fromStore, tariff?.integrationId)
            return TARIFF_CHANNEL_SLOTS[logicalId] || 0
        }
        const needsDowngradeWizard = computed(() => {
            const currentCount = getTariffChannelSlotCount(currentTariffData.value)
            const nextCount = getTariffChannelSlotCount(selectedTariff.value)
            return currentCount > nextCount
        })
        const tariffPickerPeriodHint = computed(() => {
            const periodNum = Number(billingStore.selectedSubscriptionPeriod || 1)
            const periodLabel = getFormatedPeriodLabel(periodNum)
            if (!periodLabel) return ''
            return `при оплате за ${periodLabel.replace(/^на\s+/, '')}`
        })
        const getTariffMonthlyPrice = (tariff: {
            price?: number
            complexPrice?: {
                monthPeriod?: Record<string, { regularPayment?: { sum?: number } }>
            } | null
        }) => {
            const period = String(billingStore.selectedSubscriptionPeriod || '1')
            const months = Number(period) || 1
            const periodSum = Number(tariff?.complexPrice?.monthPeriod?.[period]?.regularPayment?.sum)
            if (Number.isFinite(periodSum) && periodSum > 0 && months > 0) {
                return Math.round(periodSum / months)
            }
            const monthlyBase =
                Number(tariff?.complexPrice?.monthPeriod?.['1']?.regularPayment?.sum) ||
                Number(tariff?.price) ||
                0
            const percent = getPeriodDiscountPercent(tariff, period) || 0
            return Math.round(monthlyBase * (1 - percent / 100))
        }
        const tariffPickerOptions = computed(() => {
            if (!showTariffPicker.value) return []
            const currentId = String(currentTariffData.value?.integrationId || '')
            const currentFromStore = currentId ? billingStore.getTariffById(currentId) : undefined
            const currentLogicalId = getTariffLogicalId(currentFromStore, currentId)
            const tariffs = [...(billingStore.tariffs || [])]
            const visibleTariffs =
                currentLogicalId === 'tariff1'
                    ? tariffs
                          .filter(tariff => ['tariff1', 'tariff2'].includes(getTariffLogicalId(tariff)))
                          .sort(
                              (left, right) =>
                                  (TARIFF_PICKER_ORDER[getTariffLogicalId(left)] || 99) -
                                  (TARIFF_PICKER_ORDER[getTariffLogicalId(right)] || 99)
                          )
                    : tariffs.filter(tariff => tariff.id === currentId)
            const options = visibleTariffs.map(tariff => {
                const logicalId = getTariffLogicalId(tariff)
                const isCurrent = tariff.id === currentId || logicalId === currentLogicalId
                return {
                    id: tariff.id,
                    name: tariff.name,
                    subtitle: isCurrent
                        ? 'Текущий тариф'
                        : logicalId === 'tariff2'
                          ? 'Больше каналов, аналитика и KPI'
                          : 'Тариф',
                    priceLabel: `${billingStore.formatPrice(getTariffMonthlyPrice(tariff))}/мес.`,
                    periodHint: tariffPickerPeriodHint.value,
                }
            })
            if (options.length) return options
            if (!currentId) return []
            return [
                {
                    id: currentId,
                    name: currentTariffData.value?.name || 'Текущий тариф',
                    subtitle: 'Текущий тариф',
                    priceLabel: '',
                    periodHint: tariffPickerPeriodHint.value,
                },
            ]
        })
        const getPeriodDiscountLabel = (period: string) =>
            getSubscriptionPeriodDiscountLabel(period, selectedTariff.value ?? null)
        const allSubscriptionPeriodPills = computed(() =>
            getSubscriptionPeriodPillItems(selectedTariff.value ?? null, {
                excludeMonthly: periodRestrictionActive.value,
            })
        )
        const currentPeriodComparableMonths = computed(() =>
            comparableSubscriptionPeriodMonths(currentTariffData.value?.periodInMonths)
        )
        const shouldHideShorterPeriods = computed(
            () =>
                Boolean(renewal.value) &&
                props.mode !== 'add-channel' &&
                currentPeriodComparableMonths.value > 1 &&
                !revealShorterPeriods.value
        )
        const shorterPeriodPills = computed(() => {
            const minMonths = currentPeriodComparableMonths.value
            return allSubscriptionPeriodPills.value.filter(
                item => comparableSubscriptionPeriodMonths(item.value) < minMonths
            )
        })
        const subscriptionPeriodPills = computed(() => {
            const pills = allSubscriptionPeriodPills.value
            if (!shouldHideShorterPeriods.value) return pills
            const minMonths = currentPeriodComparableMonths.value
            const base = pills.filter(
                item => comparableSubscriptionPeriodMonths(item.value) >= minMonths
            )
            if (!extraRevealedPeriodValues.value.length) return base
            const revealed = extraRevealedPeriodValues.value
            const shownShorter = pills
                .filter(item => revealed.includes(item.value))
                .sort((a, b) => Number(a.value) - Number(b.value))
            return [...shownShorter, ...base]
        })
        const hasHiddenShorterPeriods = computed(() => {
            if (!renewal.value || props.mode === 'add-channel') return false
            const minMonths = currentPeriodComparableMonths.value
            if (minMonths <= 1) return false
            return shorterPeriodPills.value.length > 0
        })
        const showRevealShorterPeriodsButton = computed(
            () =>
                Boolean(billingStore.paymentModal.allowShorterPeriods) &&
                hasHiddenShorterPeriods.value &&
                !revealShorterPeriods.value &&
                !isRevealingShorterPeriods.value
        )
        const revealShorterStaged = async () => {
            if (isRevealingShorterPeriods.value || revealShorterPeriods.value) return
            const toInsert = [...shorterPeriodPills.value].sort(
                (a, b) => Number(b.value) - Number(a.value)
            )
            if (!toInsert.length) {
                revealShorterPeriods.value = true
                return
            }
            const generation = ++revealShorterGeneration
            isRevealingShorterPeriods.value = true
            const reduceMotion =
                typeof window !== 'undefined' &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches
            if (reduceMotion || !periodRowRef.value) {
                extraRevealedPeriodValues.value = toInsert.map(item => item.value)
                revealShorterPeriods.value = true
                extraRevealedPeriodValues.value = []
                isRevealingShorterPeriods.value = false
                return
            }
            const measured = measurePeriodRow()
            if (!measured) {
                extraRevealedPeriodValues.value = toInsert.map(item => item.value)
                revealShorterPeriods.value = true
                extraRevealedPeriodValues.value = []
                isRevealingShorterPeriods.value = false
                return
            }
            periodAnimLayout = { rowWidth: measured.rowWidth, height: measured.height }
            lockPeriodStageHeight(measured.height)
            for (const pill of toInsert) {
                if (generation !== revealShorterGeneration) return
                const ok = await splitLeftmostPeriod(pill, generation)
                if (!ok) {
                    extraRevealedPeriodValues.value = toInsert.map(item => item.value)
                    break
                }
            }
            if (generation !== revealShorterGeneration) return
            splitAnimActive.value = false
            splitClones.value = []
            revealShorterPeriods.value = true
            extraRevealedPeriodValues.value = []
            isRevealingShorterPeriods.value = false
            periodAnimLayout = null
            unlockPeriodStageHeight()
        }
        type PeriodBox = { left: number; top: number; width: number; height: number }
        type PeriodClone = {
            id: string
            label: string
            discount: string | null
            active: boolean
            style: Record<string, string>
        }
        const boxStyle = (box: PeriodBox, zIndex = '1'): Record<string, string> => ({
            left: `${box.left}px`,
            top: `${box.top}px`,
            width: `${box.width}px`,
            height: `${box.height}px`,
            zIndex,
        })
        const equalPeriodBoxes = (count: number, rowWidth: number, height: number): PeriodBox[] => {
            const width = Math.max(0, (rowWidth - PERIOD_GAP * Math.max(0, count - 1)) / count)
            return Array.from({ length: count }, (_, index) => ({
                left: index * (width + PERIOD_GAP),
                top: 0,
                width,
                height,
            }))
        }
        const measurePeriodRow = () => {
            const row = periodRowRef.value
            if (!row) return null
            const pills = [...row.querySelectorAll<HTMLElement>('.pill')]
            if (!pills.length) return null
            const rowRect = row.getBoundingClientRect()
            return {
                rowWidth: rowRect.width,
                height: pills[0].getBoundingClientRect().height,
                boxes: pills.map(el => {
                    const rect = el.getBoundingClientRect()
                    return {
                        left: rect.left - rowRect.left,
                        top: rect.top - rowRect.top,
                        width: rect.width,
                        height: rect.height,
                    }
                }),
            }
        }
        const makeClone = (
            item: { value: string; label: string },
            id: string,
            box: PeriodBox,
            zIndex = '1'
        ): PeriodClone => ({
            id,
            label: item.label,
            discount: getPeriodDiscountLabel(item.value),
            active: billingStore.selectedSubscriptionPeriod === item.value,
            style: boxStyle(box, zIndex),
        })
        const getCloneNode = (id: string) =>
            periodStageRef.value?.querySelector<HTMLElement>(`[data-clone-id="${id}"]`) ?? null
        const animateCloneBox = (
            el: HTMLElement | null,
            from: PeriodBox,
            to: PeriodBox,
            duration: number
        ): Promise<void> => {
            if (!el) return Promise.resolve()
            return el
                .animate(
                    [
                        {
                            left: `${from.left}px`,
                            width: `${from.width}px`,
                        },
                        {
                            left: `${to.left}px`,
                            width: `${to.width}px`,
                        },
                    ],
                    {
                        duration,
                        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                        fill: 'forwards',
                    }
                )
                .finished.then(() => undefined)
        }
        const splitLeftmostPeriod = async (
            incoming: { value: string; label: string },
            generation: number
        ) => {
            const currentPills = subscriptionPeriodPills.value
            const leftmost = currentPills[0]
            const layout = periodAnimLayout
            if (!leftmost || !layout) return false
            const startBoxes = equalPeriodBoxes(currentPills.length, layout.rowWidth, layout.height)
            const endBoxes = equalPeriodBoxes(currentPills.length + 1, layout.rowWidth, layout.height)
            const leftDaughter = endBoxes[0]
            const rightDaughter = endBoxes[1]
            if (!startBoxes[0] || !leftDaughter || !rightDaughter) return false
            const grownBox: PeriodBox = {
                left: leftDaughter.left,
                top: 0,
                width: rightDaughter.left + rightDaughter.width - leftDaughter.left,
                height: layout.height,
            }
            const siblingTargets = currentPills.slice(1).map((_, index) => endBoxes[index + 2])
            splitClones.value = currentPills.map((item, index) =>
                makeClone(item, `grow-${item.value}`, startBoxes[index] || startBoxes[0], index === 0 ? '3' : '1')
            )
            splitAnimActive.value = true
            await nextTick()
            await waitNextFrame()
            if (generation !== revealShorterGeneration) return false
            await Promise.all([
                animateCloneBox(getCloneNode(`grow-${leftmost.value}`), startBoxes[0], grownBox, 120),
                ...currentPills.slice(1).map((item, index) => {
                    const from = startBoxes[index + 1]
                    const to = siblingTargets[index] || endBoxes[index + 2]
                    if (!from || !to) return Promise.resolve()
                    return animateCloneBox(getCloneNode(`grow-${item.value}`), from, to, 120)
                }),
            ])
            if (generation !== revealShorterGeneration) return false
            await wait(16)
            splitClones.value = [
                makeClone(incoming, `new-${incoming.value}`, leftDaughter, '2'),
                makeClone(leftmost, `keep-${leftmost.value}`, grownBox, '4'),
                ...currentPills.slice(1).map((item, index) =>
                    makeClone(
                        item,
                        `rest-${item.value}`,
                        siblingTargets[index] || endBoxes[index + 2],
                        '1'
                    )
                ),
            ]
            await nextTick()
            await waitNextFrame()
            if (generation !== revealShorterGeneration) return false
            await animateCloneBox(
                getCloneNode(`keep-${leftmost.value}`),
                grownBox,
                rightDaughter,
                148
            )
            if (generation !== revealShorterGeneration) return false
            extraRevealedPeriodValues.value = [...extraRevealedPeriodValues.value, incoming.value]
            await nextTick()
            await wait(12)
            return true
        }
        const isSelectedPeriodShorterThanCurrent = () => {
            const currentMonths = currentPeriodComparableMonths.value
            const selectedMonths = comparableSubscriptionPeriodMonths(
                billingStore.selectedSubscriptionPeriod
            )
            return currentMonths > 1 && selectedMonths > 0 && selectedMonths < currentMonths
        }
        const planSectionLabel = computed(() =>
            renewalCopy.value ? 'Состав продления:' : 'Новый тариф:'
        )
        const addChannelContext = computed(() => billingStore.addChannelContext)
        const addChannelIntegrationId = computed(
            () => addChannelContext.value?.integrationId || 'channelSlot'
        )
        const moduleIds = config.marketplaceModulesIds[config.buildType]
        const isDhChatIntegration = (integrationId?: string | null) =>
            String(integrationId || '') === String(moduleIds.dhChat)
        const isCustomCascadeIntegration = (integrationId?: string | null) =>
            String(integrationId || '') === String(moduleIds.customCascades)
        const isZvonoBotIntegration = (integrationId?: string | null) =>
            String(integrationId || '') === String(moduleIds.zvonoBot)
        const isBusinessPulseReportIntegration = (integrationId?: string | null) =>
            String(integrationId || '') === String(moduleIds.businessPulseReport)
        const isHotWindowsIntegration = (integrationId?: string | null) =>
            String(integrationId || '') === String(moduleIds.hotWindows)
        const isOpz2Integration = (integrationId?: string | null) =>
            String(integrationId || '') === String(moduleIds.opz2)
        const isOpz2KpiSettingsIntegration = (integrationId?: string | null) =>
            String(integrationId || '') === String(moduleIds.opz2KPISettings)
        const isZvonoBotSettingsIntegration = (integrationId?: string | null) =>
            String(integrationId || '') === String(moduleIds.zvonoBotSettings)
        const showOpz2KpiAddon = computed(
            () =>
                props.mode === 'add-channel' &&
                !renewal.value &&
                isOpz2Integration(addChannelContext.value?.integrationId),
        )
        const isOpz2KpiSettingsPurchase = computed(
            () =>
                props.mode === 'add-channel' &&
                isOpz2KpiSettingsIntegration(addChannelContext.value?.integrationId),
        )
        // Планы Beauty Autopilot продаются и без тарифа: тогда только отдельно каждый месяц (standalone).
        const isBeautyAutopilotPlanPurchase = computed(
            () =>
                props.mode === 'add-channel' &&
                Object.values(BEAUTY_AUTOPILOT_PLAN_INTEGRATION_IDS).includes(
                    String(addChannelContext.value?.integrationId || ''),
                ),
        )
        const isBeautyAutopilotStandaloneOnly = computed(
            () => isBeautyAutopilotPlanPurchase.value && !currentTariffData.value,
        )
        const showZvonoBotSettingsAddon = computed(
            () =>
                props.mode === 'add-channel' &&
                !renewal.value &&
                isZvonoBotIntegration(addChannelContext.value?.integrationId),
        )
        const isZvonoBotSettingsPurchase = computed(
            () =>
                props.mode === 'add-channel' &&
                isZvonoBotSettingsIntegration(addChannelContext.value?.integrationId),
        )
        const addOpz2KpiSettings = ref(false)
        const addZvonoBotSettings = ref(false)
        const settingsRequestKind = ref<'opz2-kpi' | 'zvono-bot' | null>(null)
        const pendingAfterSettingsRequest = ref<'pay' | 'invoice' | null>(null)
        const kpiSettingsIntegrationId = String(moduleIds.opz2KPISettings || '')
        const zvonoBotSettingsIntegrationId = String(moduleIds.zvonoBotSettings || '')
        const marketplaceIntegrationById = (integrationId: string) => {
            const projectId = project.value?.id
            if (!projectId || !integrationId) return null
            return (
                useMarketplaceStore()
                    .allIntgrations(projectId)
                    .find(item => String(item.id) === integrationId) ?? null
            )
        }
        const integrationPriceLabel = (integration: { price?: number | string; paymentType?: string } | null) => {
            const price = Number(integration?.price)
            if (!Number.isFinite(price) || price <= 0) return ''
            const formatted = billingStore.formatPrice(price)
            return integration?.paymentType === 'subscription' ? `${formatted}/мес.` : formatted
        }
        const kpiSettingsIntegration = computed(() => marketplaceIntegrationById(kpiSettingsIntegrationId))
        const zvonoBotSettingsIntegration = computed(() =>
            marketplaceIntegrationById(zvonoBotSettingsIntegrationId),
        )
        const kpiSettingsPriceLabel = computed(() => integrationPriceLabel(kpiSettingsIntegration.value))
        const zvonoBotSettingsPriceLabel = computed(() =>
            integrationPriceLabel(zvonoBotSettingsIntegration.value),
        )
        const extraIntegrationIds = computed(() => {
            const ids: string[] = []
            if (showOpz2KpiAddon.value && addOpz2KpiSettings.value && kpiSettingsIntegrationId) {
                ids.push(kpiSettingsIntegrationId)
            }
            if (
                showZvonoBotSettingsAddon.value &&
                addZvonoBotSettings.value &&
                zvonoBotSettingsIntegrationId
            ) {
                ids.push(zvonoBotSettingsIntegrationId)
            }
            return ids
        })
        const lineItemPriceLabel = (integrationId: string) => {
            const lineItems = solvedPaymentData.value?.price?.lineItems
            if (!integrationId || !Array.isArray(lineItems)) return ''
            const lineItem = lineItems.find(
                (item: any) => String(item?.integrationId || '') === String(integrationId),
            )
            const amount = Number(lineItem?.amount)
            if (!Number.isFinite(amount)) return ''
            return billingStore.formatPrice(amount)
        }
        const activeSettingsRequest = computed(() => {
            if (settingsRequestKind.value === 'zvono-bot') {
                return {
                    title: 'Настройка Аудиорассылок DH',
                    priceLabel:
                        lineItemPriceLabel(zvonoBotSettingsIntegrationId) ||
                        zvonoBotSettingsPriceLabel.value,
                    integrationId: zvonoBotSettingsIntegrationId,
                    leadType: 'Агентство (лиды)',
                    leadColumn: 'Новая сделка',
                    requestName: 'Заявка МП на настройку Аудиорассылок DH',
                    formId: 'form0077',
                    missingMessage: 'Не удалось определить настройку Аудиорассылок DH',
                }
            }
            if (settingsRequestKind.value === 'opz2-kpi') {
                return {
                    title: 'Настройка KPI для ОПЗ 2.0',
                    priceLabel:
                        lineItemPriceLabel(kpiSettingsIntegrationId) || kpiSettingsPriceLabel.value,
                    integrationId: kpiSettingsIntegrationId,
                    leadType: 'Внедрение',
                    leadColumn: 'Ожидаем оплату',
                    requestName: 'Заявка МП на настройку ОПЗ 2.0',
                    formId: 'form0076',
                    missingMessage: 'Не удалось определить настройку KPI',
                }
            }
            return null
        })
        const isModuleByName = (name?: string | null) => {
            const n = String(name || '').toLowerCase()
            return n.includes('мультичат') || n.includes('каскад') || n.includes('отчёт 2.0') || n.includes('опз 2.0') || n.includes('горячие окна')
        }
        const isServiceLineItem = (lineItem: { kind?: string | null } | null | undefined) =>
            String(lineItem?.kind || '').toLowerCase() === 'service'
        const isModuleItem = (params: {
            integrationId?: string | null
            name?: string | null
            kind?: string | null
        }) =>
            isServiceLineItem(params) ? false :
            String(params.kind || '').toLowerCase() === 'module' ||
            isDhChatIntegration(params.integrationId) ||
            isCustomCascadeIntegration(params.integrationId) ||
            isBusinessPulseReportIntegration(params.integrationId) ||
            isOpz2Integration(params.integrationId) ||
            isHotWindowsIntegration(params.integrationId) ||
            isModuleByName(params.name) ||
            isOpz2KpiSettingsIntegration(params.integrationId) ||
            isZvonoBotSettingsIntegration(params.integrationId) ||
            isZvonoBotIntegration(params.integrationId)
        const getChannelSubtitle = (
            integrationId?: string | null,
            isInTariff = false,
            name?: string | null,
            kind?: string | null
        ) => {
            if (String(kind || '').toLowerCase() === 'service') return 'Услуга'
            if (isInTariff) return 'Входит в тариф'
            return isModuleItem({ integrationId, name, kind }) ? 'Модуль' : 'Доп. канал'
        }
        const tariffDisplayName = computed(() => selectedTariff.value?.name ?? '—')
        const tariffIconUrl = require('@/assets/icons/Icn_M_Briefcase.svg')
        const whatsappChannelIconUrl = require('@/assets/icons/whatsApp-white.svg')
        const telegramChannelIconUrl = require('@/assets/icons/telegram-white.svg')
        const maxChannelIconUrl = require('@/assets/icons/max-white.svg')
        const smsChannelIconUrl = require('@/assets/img/messenger-logo/sms-logo.png')
        const isMaxBusinessChannel = (params: {
            name?: string | null
            kind?: string | null
            integrationId?: string | null
        }) => {
            const kind = String(params.kind || '').toLowerCase()
            const integrationId = String(params.integrationId || '').toLowerCase()
            const name = String(params.name || '').toLowerCase()
            return (
                kind === 'maxbusiness' ||
                kind === 'max' ||
                integrationId.includes('maxbusiness') ||
                name.includes('max business') ||
                name.includes('maxbusiness') ||
                (name.includes('max') && !isUnassignedChannelSlotName(params.name))
            )
        }
        const currentChannelName = computed(() => addChannelContext.value?.name || tariffDisplayName.value)
        const currentChannelSubtitle = computed(() =>
            getChannelSubtitle(
                addChannelIntegrationId.value,
                false,
                addChannelContext.value?.name,
                addChannelContext.value?.itemKind
            )
        )
        const addChannelBillingSectionTitle = computed(() =>
            currentChannelSubtitle.value === 'Модуль' ? 'Как оплачивать модуль' : 'Как оплачивать канал'
        )
        const addChannelBundleHint = computed(
            () =>
                `${currentChannelName.value} появится в составе подписки. При продлении тарифа он будет добавлен в корзину с отмеченной галочкой, но его можно исключить.`
        )
        const addChannelMonthlyHint = computed(
            () =>
                `${currentChannelName.value} останется в разделе дополнительных подписок. У него будет собственная дата оплаты и отдельное автопродление.`
        )
        const isSmsChannel = (params: {
            name?: string | null
            kind?: string | null
            integrationId?: string | null
        }) => {
            if (isUnassignedChannelSlotName(params.name)) return false
            const kind = String(params.kind || '').toLowerCase()
            const name = String(params.name || '').toLowerCase()
            const integrationId = String(params.integrationId || '')
            return kind === 'sms' || name.includes('sms') || integrationId === String(moduleIds.sms)
        }
        const currentChannelIconSrc = computed(() => {
            if (addChannelContext.value?.icon) return addChannelContext.value.icon
            const context = addChannelContext.value
            const name = String(context?.name || '').toLowerCase()
            if (name.includes('whatsapp')) return whatsappChannelIconUrl
            if (name.includes('telegram')) return telegramChannelIconUrl
            if (
                isSmsChannel({
                    name: context?.name,
                    kind: context?.kind,
                    integrationId: context?.integrationId,
                })
            ) {
                return smsChannelIconUrl
            }
            if (
                isMaxBusinessChannel({
                    name: context?.name,
                    kind: context?.kind,
                    integrationId: context?.integrationId,
                })
            ) {
                return maxChannelIconUrl
            }
            return tariffIconUrl
        })
        const currentChannelKind = computed(() => {
            const context = addChannelContext.value
            if (
                isSmsChannel({
                    name: context?.name,
                    kind: context?.kind,
                    integrationId: context?.integrationId,
                })
            ) {
                return 'sms'
            }
            const explicitKind = context?.kind
            if (explicitKind && explicitKind !== 'bundle') return explicitKind

            const icon = (currentChannelIconSrc.value || '').toLowerCase()
            if (icon.includes('whatsapp')) return 'whatsapp'
            if (icon.includes('telegram')) return 'telegram'
            if (icon.includes('max')) return 'max'
            return 'tariff'
        })
        const currentChannelIconClass = computed(() => {
            const map: Record<string, string> = {
                tariff: 'plan-row__icon--tariff',
                whatsapp: 'plan-row__icon--wa',
                telegram: 'plan-row__icon--tg',
                max: 'plan-row__icon--max',
                maxbusiness: 'plan-row__icon--max',
                sms: 'plan-row__icon--sms',
            }
            if (map[currentChannelKind.value]) return map[currentChannelKind.value]
            if (
                isMaxBusinessChannel({
                    name: addChannelContext.value?.name,
                    kind: addChannelContext.value?.kind,
                    integrationId: addChannelContext.value?.integrationId,
                })
            ) {
                return map.max
            }
            return map.tariff
        })
        const formattedTodayDate = computed((): string => {
            if (currentTariffData.value && !currentTariffData.value?.isTrial && renewal.value) {
                return new Date(currentTariffData.value.activeUntil).toLocaleDateString('ru-RU')
            }
            return new Date().toLocaleDateString('ru-RU')
        })
        const tariffDisplaySubtitle = computed(() => `с ${formattedTodayDate.value}`)
        const headerPeriodLabel = computed(() => {
            const periodKey = billingStore.selectedSubscriptionPeriod ?? '1'
            const periodNum = Number(periodKey)
            return getFormatedPeriodLabel(periodNum)
        })
        const tablePeriodRange = computed(() => {
            const activeFrom = solvedPaymentData.value?.activeFrom
            const activeUntil = solvedPaymentData.value?.activeUntil
            if (activeFrom && activeUntil) {
                const startDate = new Date(activeFrom)
                const endDate = new Date(activeUntil)
                if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
                    const format = (date: Date) => date.toLocaleDateString('ru-RU')
                    return `${format(startDate)}—${format(endDate)}`
                }
            }
            const periodKey = billingStore.selectedSubscriptionPeriod ?? '1'
            const periodNum = Number(periodKey)
            if (!periodNum) return ''
            // начало периода совпадает с formattedTodayDate (как в старой модалке)
            const start = formattedTodayDate.value
            const [d, m, y] = start.split('.')
            const startDate = new Date(Number(y), Number(m) - 1, Number(d))
            const end = new Date(startDate.getTime())
            end.setMonth(end.getMonth() + periodNum)
            const format = (date: Date) => date.toLocaleDateString('ru-RU')
            return `${format(startDate)}—${format(end)}`
        })
        const formatDateRu = (value?: string | null) => {
            if (!value) return ''
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return ''
            return date.toLocaleDateString('ru-RU')
        }
        const addChannelTariffWarningText = computed(() => {
            const start = formatDateRu(currentTariffData.value?.startOfUse)
            const end = formatDateRu(
                currentTariffData.value?.endOfUse || currentTariffData.value?.activeUntil
            )
            const channelName = String(addChannelContext.value?.name || '').toLowerCase()
            const warningCopy = [
                { match: 'мультичат', detached: 'Мультичат', subscription: 'подписки на мультичат' },
                { match: 'каскад', detached: 'Дополнительные каскады', subscription: 'подписки на дополнительные каскады' },
                { match: 'отчёт 2.0', detached: 'Отчёт 2.0', subscription: 'подписки Отчёта 2.0' },
                { match: 'опз 2.0', detached: 'ОПЗ 2.0', subscription: 'подписки ОПЗ 2.0' },
                { match: 'звонобот', detached: 'Аудиорассылки DH', subscription: 'подписки Аудиорассылки DH' },
                { match: 'аудиорассылки', detached: 'Аудиорассылки DH', subscription: 'подписки Аудиорассылки DH' },
            ].find(item => channelName.includes(item.match))
            const detachedChannelLabel = warningCopy?.detached ?? 'Отдельный канал'
            const newChannelLabel = warningCopy?.subscription ?? 'нового канала'
            if (isZeroDayPeriodRange.value) {
                if (!end) return ''
                return `Ваш текущий тариф оплачен до ${end}\n${detachedChannelLabel} не может выходить за срок действия тарифа. Сначала оплатите подписку с тарифом`
            }
            if (!start || !end) return ''
            return `Ваш текущий тариф оплачен с ${start} по ${end}\n\nОплата ${newChannelLabel} рассчитана до конца текущего тарифа. Далее будет вместе с тарифом.`
        })

        const getFormatedPeriod = (n: number): string => {
            switch (n) {
                case 1:
                    return 'на месяц'
                case 3:
                    return 'на 3 месяца'
                case 6:
                    return 'на 6 месяцев'
                case 12:
                    return 'на 12 месяцев'
                case 24:
                    return 'на 24 месяца'
                default:
                    return ''
            }
        }

        const periodCheckIcon = require('@/assets/icons/period-check.svg')

        const selectedPeriod = computed(() => billingStore.selectedSubscriptionPeriod ?? '1')
        const isPeriodSelected = (period: string) => billingStore.selectedSubscriptionPeriod === period

        const addChannelOption = ref<'bundle' | 'monthly'>('bundle')
        const isEndTariff = computed(() => {
            if (!project.value?.billingEnabled) return false
            if (isBeautyAutopilotPlanPurchase.value) return false
            return !currentTariffData.value
        })
        const normalizeSubscriptionPeriod = (period?: string | null) =>
            resolveSubscriptionPeriod(period, selectedTariff.value ?? null, periodResolveOptions.value)
        const selectAddChannelOption = async (option: 'bundle' | 'monthly') => {
            addChannelOption.value = option
            await loadSolvePayment()
        }
        const isAddChannelOptionSelected = (option: 'bundle' | 'monthly') => addChannelOption.value === option

        const invoicePeriod = computed(() => {
            if (props.mode === 'add-channel') {
                return resolveAddChannelInvoicePeriod(
                    addChannelOption.value,
                    currentTariffData.value?.periodInMonths
                )
            }
            return resolveBillingInvoicePeriod({
                subscriptionPeriod: solvedPaymentData.value?.subscriptionPeriod,
                isFirstPurchase: solvedPaymentData.value?.price?.isFirstPurchase,
                selectedSubscriptionPeriod: billingStore.selectedSubscriptionPeriod,
            })
        })
        const invoicePrice = computed(() => {
            const value = solvedPaymentData.value?.price?.toPay
            return String(typeof value === 'number' ? value : 0)
        })
        const invoicePaymentPayloadId = computed(() => {
            const value = (solvedPaymentData.value as any)?.paymentPayloadId
            return String(value || '')
        })
        const projectIdForInvoice = computed(() => String(project.value?.id ?? ''))
        const projNameForInvoice = computed(() => String(project.value?.name ?? ''))

        const loading = ref(false)
        const solvedPaymentData = ref<any>(null)
        const invoiceSubscriptions = ref<any>(null)
        const paymentLinkLoading = ref(false)
        const isDetachChannelModalOpened = ref(false)
        const detachChannelModalChannelName = ref('')
        const detachChannelModalSubscriptionId = ref<number | null>(null)
        const detachChannelModalLoading = ref(false)
        const detachChannelModalIsModule = ref(false)
        const detachChannelModalInTariff = ref(false)
        const detachChannelModalLineKey = ref('')
        const keepAddonSubscriptionIds = ref<number[] | null>(null)
        const knownAddonSubscriptionIds = ref<number[]>([])
        const excludedAddonLines = ref<Array<{
            subscriptionId: number
            name: string
            meta?: string
            period?: string
            amount: number | string
            slotId?: string
            isModule?: boolean
            order: number
            periodRange?: string
        }>>([])
        const formatLineItemPeriodRange = (lineItem: any) => {
            const periodFrom = lineItem?.periodFrom ? new Date(lineItem.periodFrom) : null
            const periodTo = lineItem?.periodTo ? new Date(lineItem.periodTo) : null
            const hasValidDates =
                periodFrom &&
                periodTo &&
                !Number.isNaN(periodFrom.getTime()) &&
                !Number.isNaN(periodTo.getTime())
            return hasValidDates
                ? `${periodFrom.toLocaleDateString('ru-RU')}—${periodTo.toLocaleDateString('ru-RU')}`
                : String(lineItem?.period || '')
        }
        const formatExcludedAmount = (amount: number | string | undefined) => {
            const rawAmount = typeof amount === 'number' ? amount : Number(amount)
            return billingStore.formatPrice(Number.isFinite(rawAmount) ? rawAmount : 0)
        }
        const collectAddonSubscriptionIds = (lineItems: unknown) => {
            if (!Array.isArray(lineItems)) return [] as number[]
            return lineItems
                .filter((item: any) => (
                    item
                    && item.kind !== 'tariff'
                    && item.isTariff !== true
                    && !item.inTariff
                    && typeof item.subscriptionId === 'number'
                ))
                .map((item: any) => Number(item.subscriptionId))
        }
        const resetAddonExclusions = () => {
            keepAddonSubscriptionIds.value = null
            knownAddonSubscriptionIds.value = []
            excludedAddonLines.value = []
        }
        const isDowngradeModalOpened = ref(false)
        const downgradeModalLoading = ref(false)
        const downgradeModalSuccess = ref(false)
        const downgradeModalPaymentUrl = ref('')
        const isScheduledBlockedModalOpened = ref(false)
        const checkingScheduledChange = ref(false)
        const isInvoice = ref(false)
        const selectedPayment = ref<string | number | null>('card')
        const contractors = ref<any[]>([])
        const selectedContractor = ref<any | null>(null)
        const invoiceBillingEnabled = ref(true)

        const setDefaultPeriod = () => {
            // Если период был задан снаружи (например, из вкладки «Подписки»),
            // не перетираем его дефолтом.
            if (billingStore.selectedSubscriptionPeriod === '2') {
                billingStore.selectedSubscriptionPeriod = '1'
            }
            if (billingStore.selectedSubscriptionPeriod) {
                billingStore.selectedSubscriptionPeriod = normalizeSubscriptionPeriod(
                    billingStore.selectedSubscriptionPeriod
                )
                return
            }
            if (currentTariffData.value?.periodInMonths && !currentTariffData.value?.isTrial) {
                billingStore.selectedSubscriptionPeriod = normalizeSubscriptionPeriod(
                    currentTariffData.value.periodInMonths.toString()
                )
            } else {
                billingStore.selectedSubscriptionPeriod = normalizeSubscriptionPeriod(null)
            }
        }

        const loadSolvePayment = async () => {
            if (
                props.mode !== 'add-channel' &&
                (!project.value?.id ||
                    !billingStore.selectedTariffId ||
                    !billingStore.selectedSubscriptionPeriod ||
                    !user.value?.sub)
            )
                return
            if (!project.value?.id || !user.value?.sub) return
            loading.value = true
            invoiceBillingEnabled.value = true

            const solveMethod = renewal.value
                ? api.marketplace.solveRenewalSubscription
                : api.marketplace.solvePayment
            const params: any = {
                projectId: project.value.id,
                marketplaceIntegrationId:
                    props.mode === 'add-channel'
                        ? addChannelIntegrationId.value
                        : billingStore.selectedTariffId,
                subscriptionPeriod:
                    props.mode === 'add-channel' && (addChannelOption.value === 'monthly' || isBeautyAutopilotStandaloneOnly.value)
                        ? '1'
                        : billingStore.selectedSubscriptionPeriod,
                userId: user.value.sub,
            }
            if (props.mode === 'add-channel') {
                params.renewalMode = isOpz2KpiSettingsPurchase.value || isBeautyAutopilotStandaloneOnly.value
                    ? 'standalone'
                    : addChannelOption.value === 'bundle'
                        ? 'with_tariff'
                        : 'standalone'
            }
            if (renewal.value) {
                if (props.mode === 'add-channel') {
                    const channelSubscriptionId = addChannelContext.value?.subscriptionId
                    if (typeof channelSubscriptionId === 'number') {
                        params.subscriptionId = channelSubscriptionId
                    }
                } else if (currentTariffData.value?.subscriptionId) {
                    params.subscriptionId = currentTariffData.value.subscriptionId
                }
            }
            if (keepAddonSubscriptionIds.value !== null) {
                params.keepAddonSubscriptionIds = keepAddonSubscriptionIds.value
            }
            if (extraIntegrationIds.value.length) {
                params.extraIntegrationIds = extraIntegrationIds.value
            }
            const { data, error } = await solveMethod(params)
            loading.value = false
            if (error) {
                root?.$store?.dispatch('callNotify', 'Ошибка при загрузке данных о платеже')
                return
            }
            solvedPaymentData.value = data
            const addonIds = collectAddonSubscriptionIds((data as any)?.price?.lineItems)
            if (keepAddonSubscriptionIds.value === null) {
                knownAddonSubscriptionIds.value = addonIds
            } else {
                knownAddonSubscriptionIds.value = Array.from(new Set([
                    ...knownAddonSubscriptionIds.value,
                    ...addonIds,
                ]))
            }

            if (renewal.value) {
                const subscriptionsResponse = await api.marketplace.getSubscriptions(project.value.id)
                const subscriptionsPayload = subscriptionsResponse?.data
                invoiceSubscriptions.value =
                    subscriptionsPayload && 'data' in subscriptionsPayload
                        ? subscriptionsPayload.data
                        : subscriptionsPayload
            } else {
                invoiceSubscriptions.value = null
            }
        }

        // Сравниваем состав, а не массив: computed отдаёт новый массив на каждый пересчёт, и watcher
        // срабатывал бы на любую смену контекста — при открытии раньше watcher'а modalOpened, со способом
        // оплаты от прошлого открытия.
        watch(
            () => extraIntegrationIds.value.join(','),
            () => {
                if (!modalOpened.value || !shouldRenderSidebar.value) return
                void loadSolvePayment()
            },
        )

        const selectCheckoutTariff = async (tariffId: string) => {
            if (!tariffId || billingStore.selectedTariffId === tariffId) return
            billingStore.selectedTariffId = tariffId
            billingStore.selectedSubscriptionPeriod = normalizeSubscriptionPeriod(
                billingStore.selectedSubscriptionPeriod
            )
            resetAddonExclusions()
            await loadSolvePayment()
        }

        const selectPeriod = async (period: string) => {
            if (billingStore.selectedSubscriptionPeriod === period) return
            billingStore.selectedSubscriptionPeriod = period
            await loadSolvePayment()
        }

        const isZeroDayPeriodRange = computed(() => {
            if (props.mode !== 'add-channel') return false
            const proratedDays = solvedPaymentData.value?.price?.proratedDays
            if (typeof proratedDays === 'number') {
                return proratedDays <= 0
            }

            const activeFrom = solvedPaymentData.value?.activeFrom
            const activeUntil = solvedPaymentData.value?.activeUntil
            if (activeFrom && activeUntil) {
                const startDate = new Date(activeFrom)
                const endDate = new Date(activeUntil)
                if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
                    const startOfStart = new Date(
                        startDate.getFullYear(),
                        startDate.getMonth(),
                        startDate.getDate()
                    )
                    const startOfEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
                    const diffDays = Math.floor(
                        (startOfEnd.getTime() - startOfStart.getTime()) / (24 * 60 * 60 * 1000)
                    )
                    return diffDays <= 0
                }
            }

            const lineItems = solvedPaymentData.value?.price?.lineItems
            if (Array.isArray(lineItems)) {
                return lineItems.some((lineItem: any) => {
                    const periodFrom = lineItem?.periodFrom ? new Date(lineItem.periodFrom) : null
                    const periodTo = lineItem?.periodTo ? new Date(lineItem.periodTo) : null
                    if (
                        !periodFrom ||
                        !periodTo ||
                        Number.isNaN(periodFrom.getTime()) ||
                        Number.isNaN(periodTo.getTime())
                    ) {
                        return false
                    }
                    const startOfStart = new Date(
                        periodFrom.getFullYear(),
                        periodFrom.getMonth(),
                        periodFrom.getDate()
                    )
                    const startOfEnd = new Date(
                        periodTo.getFullYear(),
                        periodTo.getMonth(),
                        periodTo.getDate()
                    )
                    const diffDays = Math.floor(
                        (startOfEnd.getTime() - startOfStart.getTime()) / (24 * 60 * 60 * 1000)
                    )
                    return diffDays <= 0
                })
            }

            return false
        })

        const shouldShowPaymentMethodSelection = computed(() => invoiceBillingEnabled.value)
        const isSidebarBusy = computed(() => loading.value)
        const isPaymentActionDisabled = computed(() => {
            const baseDisabled =
                loading.value ||
                paymentLinkLoading.value ||
                checkingScheduledChange.value ||
                !solvedPaymentData.value ||
                isZeroDayPeriodRange.value
            if (props.mode !== 'add-channel') return baseDisabled
            return baseDisabled || isEndTariff.value
        })
        const requiresScheduledTariffChange = computed(
            () => solvedPaymentData.value?.requiresScheduledTariffChange === true
        )
        const isInvoicePayment = computed(
            () =>
                shouldShowPaymentMethodSelection.value &&
                selectedPayment.value &&
                selectedPayment.value !== 'card' &&
                selectedPayment.value !== 'certificate'
        )
        const paymentInfoWarningText = computed(() =>
            isInvoicePayment.value
                ? 'Информация об оплате обычно поступает в течение часа после оплаты счета'
                : 'Информация об оплате обычно поступает в течение 2-5 минут'
        )
        const scheduledTariffChangeWarningText = computed(() => {
            const tariffEndDate = currentTariffData.value?.endOfUse || currentTariffData.value?.activeUntil
            const date = tariffEndDate ? new Date(tariffEndDate) : null
            if (!date || Number.isNaN(date.getTime())) {
                return 'Текущий период уже оплачен. Изменения начнут действовать после окончания текущего тарифа'
            }
            date.setDate(date.getDate() + 1)
            return `Текущий период уже оплачен. Изменения начнут действовать с ${date.toLocaleDateString(
                'ru-RU'
            )}`
        })

        const selectPayment = (paymentId: string | number) => {
            selectedPayment.value = paymentId
            if (paymentId === 'new_invoice') {
                selectedContractor.value = null
                isInvoice.value = true
            } else {
                selectedContractor.value =
                    contractors.value.find(c => String(c.id) === String(paymentId)) || null
            }
        }

        const fetchContractors = async () => {
            const contractorsData = await api.marketplace.getContractors({ projectId: project.value.id })
            const contractorsIcon = contractorsData.data.map((c: any) => ({
                ...c,
                icon: 'mdi-account',
            }))
            contractorsIcon.push({ id: 'new_invoice', name: 'Новое Юр. лицо', icon: 'mdi-plus' })
            contractors.value = contractorsIcon
        }

        const makePayment = async () => {
            if (isPaymentActionDisabled.value) return
            paymentLinkLoading.value = true
            const { data, error } = await api.marketplace.installIntegration({
                paymentPayloadId: (solvedPaymentData.value as any)?.paymentPayloadId,
                integrationId:
                    props.mode === 'add-channel'
                        ? addChannelIntegrationId.value
                        : billingStore.selectedTariffId!,
                projectId: project.value.id,
                subscriptionPeriod: billingStore.selectedSubscriptionPeriod!,
            } as any)
            if (error) {
                root?.$store?.dispatch('callNotify', 'Ошибка при попытке оплаты')
                paymentLinkLoading.value = false
                return
            }
            if (data?.status === 'needPayment') {
                if (props.mode === 'add-channel') {
                    window.open(data.paymentLink, '_blank', 'noopener,noreferrer')
                } else {
                    // открываем ссылку в текущей вкладке, как в старой модалке
                    window.location.replace(data.paymentLink)
                }
            }
            paymentLinkLoading.value = false
            if (data?.status === 'installed') {
                root?.$store?.dispatch('callNotify', 'Подписка успешно оформлена')
                window.location.reload()
            }
        }
        const redirectAfterScheduledTariffChange = () => {
            window.location.replace(`/project/${project.value.id}/billing/?section=subscriptions`)
        }
        const closeDowngradeModal = () => {
            if (downgradeModalLoading.value) return
            isDowngradeModalOpened.value = false
            downgradeModalSuccess.value = false
            downgradeModalPaymentUrl.value = ''
        }
        const closeScheduledBlockedModal = () => {
            isScheduledBlockedModalOpened.value = false
        }
        const scheduleTariffChange = async (retainedSlotIds: string[] = []) => {
            if (isPaymentActionDisabled.value) return false
            const paymentPayloadId = Number((solvedPaymentData.value as any)?.paymentPayloadId)
            if (!Number.isFinite(paymentPayloadId) || paymentPayloadId <= 0) {
                root?.$store?.dispatch('callNotify', 'Не удалось получить данные для планирования изменений')
                return false
            }

            downgradeModalLoading.value = true
            paymentLinkLoading.value = true
            const { error, data } = await api.marketplace.scheduleTariffChange(
                paymentPayloadId,
                project.value.id,
                retainedSlotIds
            )
            paymentLinkLoading.value = false
            downgradeModalLoading.value = false
            if (error) {
                root?.$store?.dispatch('callNotify', 'Не удалось запланировать изменения')
                return false
            }
            const paymentUrl = typeof data?.paymentUrl === 'string' ? data.paymentUrl.trim() : ''
            downgradeModalPaymentUrl.value = paymentUrl
            downgradeModalSuccess.value = true
            return true
        }
        const openDowngradeModal = async () => {
            if (isPaymentActionDisabled.value || checkingScheduledChange.value) return
            checkingScheduledChange.value = true
            const alreadyScheduled = await hasScheduledTariffChange(project.value?.id)
            checkingScheduledChange.value = false
            if (alreadyScheduled) {
                isScheduledBlockedModalOpened.value = true
                return
            }
            downgradeModalSuccess.value = false
            downgradeModalPaymentUrl.value = ''
            isDowngradeModalOpened.value = true
        }
        const handleDowngradeConfirm = async (retainedSlotIds: string[] = []) => {
            const scheduled = await scheduleTariffChange(retainedSlotIds)
            if (!scheduled && !needsDowngradeWizard.value) closeDowngradeModal()
            return scheduled
        }
        const handleDowngradeSuccessClose = () => {
            closeDowngradeModal()
            redirectAfterScheduledTariffChange()
        }
        const handleDowngradePayNow = () => {
            const paymentUrl = downgradeModalPaymentUrl.value
            if (paymentUrl) {
                window.open(paymentUrl, '_blank', 'noopener,noreferrer')
            }
            handleDowngradeSuccessClose()
        }
        const settingsRequestToOpen = (): 'opz2-kpi' | 'zvono-bot' | null => {
            if (isOpz2KpiSettingsPurchase.value || (showOpz2KpiAddon.value && addOpz2KpiSettings.value)) {
                return 'opz2-kpi'
            }
            if (
                isZvonoBotSettingsPurchase.value ||
                (showZvonoBotSettingsAddon.value && addZvonoBotSettings.value)
            ) {
                return 'zvono-bot'
            }
            return null
        }
        const closeSettingsRequest = () => {
            settingsRequestKind.value = null
            pendingAfterSettingsRequest.value = null
        }
        const continueAfterSettingsRequest = () => {
            const action = pendingAfterSettingsRequest.value
            settingsRequestKind.value = null
            pendingAfterSettingsRequest.value = null
            addOpz2KpiSettings.value = false
            addZvonoBotSettings.value = false
            if (action === 'invoice') {
                return selectedContractor.value ? createInvoiceWithExistingContractor() : goToInvoice()
            }
            return makePayment()
        }
        const handlePaymentButtonClick = () => {
            if (requiresScheduledTariffChange.value) {
                return openDowngradeModal()
            }
            const requestKind = settingsRequestToOpen()
            if (requestKind) {
                pendingAfterSettingsRequest.value = 'pay'
                settingsRequestKind.value = requestKind
                return
            }
            return makePayment()
        }
        const handleInvoiceButtonClick = () => {
            if (requiresScheduledTariffChange.value) {
                return openDowngradeModal()
            }
            const requestKind = settingsRequestToOpen()
            if (requestKind) {
                pendingAfterSettingsRequest.value = 'invoice'
                settingsRequestKind.value = requestKind
                return
            }
            return selectedContractor.value ? createInvoiceWithExistingContractor() : goToInvoice()
        }
        const onPlanItemCheckboxClick = (planItem: {
            checkboxDisabled?: boolean
            included?: boolean
            displayName?: string
            subscriptionId?: number | null
            isModule?: boolean
            inTariff?: boolean
            lineKey?: string
        }) => {
            if (planItem?.checkboxDisabled) return
            if (planItem?.included === false) {
                void restoreExcludedAddon(planItem.subscriptionId)
                return
            }
            detachChannelModalChannelName.value = planItem?.displayName || 'канал'
            detachChannelModalSubscriptionId.value =
                typeof planItem?.subscriptionId === 'number' ? planItem.subscriptionId : null
            detachChannelModalIsModule.value = Boolean(planItem?.isModule)
            detachChannelModalInTariff.value = Boolean(planItem?.inTariff)
            detachChannelModalLineKey.value = planItem?.lineKey || ''
            isDetachChannelModalOpened.value = true
        }
        const closeDetachChannelModal = () => {
            isDetachChannelModalOpened.value = false
            detachChannelModalSubscriptionId.value = null
            detachChannelModalLoading.value = false
            detachChannelModalIsModule.value = false
            detachChannelModalInTariff.value = false
            detachChannelModalLineKey.value = ''
        }
        const restoreExcludedAddon = async (subscriptionId?: number | null) => {
            if (typeof subscriptionId !== 'number') return
            const currentKeep = keepAddonSubscriptionIds.value ?? knownAddonSubscriptionIds.value
            if (!currentKeep.includes(subscriptionId)) {
                keepAddonSubscriptionIds.value = [...currentKeep, subscriptionId]
            }
            await loadSolvePayment()
        }
        const handleDetachChannelCreatePackage = async () => {
            const wasModule = detachChannelModalIsModule.value
            const wasInTariff = detachChannelModalInTariff.value
            const subscriptionId = detachChannelModalSubscriptionId.value

            if (wasInTariff) {
                closeDetachChannelModal()
                root?.$store?.dispatch(
                    'callNotify',
                    'Канал не войдёт в следующий период и после его окончания исчезнет.'
                )
                return
            }

            if (!subscriptionId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить позицию для исключения из заказа')
                return
            }
            const source = planSelectionItems.value.find(item => item.subscriptionId === subscriptionId)
            if (source) {
                excludedAddonLines.value = [
                    ...excludedAddonLines.value.filter(item => item.subscriptionId !== subscriptionId),
                    {
                        subscriptionId,
                        name: source.displayName || source.name,
                        meta: source.subtitle,
                        period: source.period,
                        periodRange: source.periodRange || source.period,
                        amount: source.amount,
                        slotId: source.slotId,
                        isModule: Boolean(source.isModule || wasModule),
                        order: Number(source.order ?? planSelectionItems.value.length),
                    },
                ]
            }
            const currentKeep = keepAddonSubscriptionIds.value ?? knownAddonSubscriptionIds.value
            keepAddonSubscriptionIds.value = currentKeep.filter(id => id !== subscriptionId)
            closeDetachChannelModal()
            await loadSolvePayment()
        }

        const goToInvoice = async () => {
            if (isPaymentActionDisabled.value) return
            if (!shouldShowPaymentMethodSelection.value) {
                root?.$store?.dispatch('callNotify', 'Выставление счета недоступно для этого проекта')
                return
            }
            isInvoice.value = true
        }

        const deleteContractor = async (contractor: any) => {
            if (!confirm(`Вы уверены, что хотите удалить контрагента "${contractor.name}"?`)) return
            try {
                await api.marketplace.deleteContractor({
                    projectId: project.value.id,
                    inn: contractor.inn,
                } as any)
                await fetchContractors()
                if (selectedPayment.value === contractor.id) {
                    selectedPayment.value = ''
                    selectedContractor.value = null
                }
                root?.$store?.dispatch('callNotify', 'Контрагент успешно удален')
            } catch (e) {
                console.warn('Ошибка при удалении контрагента', e)
                root?.$store?.dispatch('callNotify', 'Ошибка при удалении контрагента')
            }
        }

        const handleInvoiceBack = async (data?: any) => {
            await fetchContractors()
            if (data?.id) {
                selectPayment(data.id)
            }
            isInvoice.value = false
        }

        const createInvoiceWithExistingContractor = async () => {
            try {
                if (isPaymentActionDisabled.value) return
                if (!selectedContractor.value) {
                    root?.$store?.dispatch('callNotify', 'Выберите контрагента для выставления счета')
                    return
                }
                if (!project.value?.id) {
                    root?.$store?.dispatch('callNotify', 'Проект не найден')
                    return
                }
                if (!invoicePaymentPayloadId.value) {
                    root?.$store?.dispatch(
                        'callNotify',
                        'Не удалось получить данные для выставления счета. Попробуйте заново открыть оплату.'
                    )
                    return
                }
                loading.value = true
                const data = {
                    inn: selectedContractor.value.inn,
                    kpp: selectedContractor.value.kpp,
                    name: selectedContractor.value.name,
                    addr: selectedContractor.value.address,
                    price: invoicePrice.value,
                    periodStart: formattedTodayDate.value,
                    period: invoicePeriod.value,
                    projectId: project.value.id,
                    paymentPayloadId: invoicePaymentPayloadId.value,
                    contractorExists: true,
                    tariff: selectedTariff.value?.name,
                    projName: project.value.name,
                    invoiceItems: buildInvoiceItemsFromPaymentContext({
                        mode: props.mode === 'add-channel' ? 'add-channel' : 'billing',
                        billingSource: renewal.value ? 'multi-subscription' : 'single-tariff',
                        totalToPay: Number(invoicePrice.value || 0),
                        tariffName:
                            selectedTariff.value?.name ||
                            currentTariffData.value?.name ||
                            currentChannelName.value,
                        currentChannelName: currentChannelName.value,
                        channelKind: addChannelContext.value?.kind,
                        paymentLineItems: solvedPaymentData.value?.price?.lineItems || [],
                        multiSubscriptionRows: renewal.value
                            ? invoiceSubscriptions.value?.multiSubscription?.rows || []
                            : [],
                    }),
                }
                await api.marketplace.createInvoice(data as any)
                loading.value = false
                root?.$store?.dispatch('callNotify', 'Счет успешно сформирован')
                root?.$root?.$emit('reload-subscriptions')
                closeModal()
            } catch (error) {
                console.warn('Ошибка при создании счета', error)
                loading.value = false
                root?.$store?.dispatch('callNotify', 'Ошибка при создании счета')
            }
        }

        const promoName = computed(() => {
            const promocodeData = solvedPaymentData.value?.price?.promocodeData
            return formatOrderPromocodes(promocodeData)
        })
        const formattedPromoDiscount = computed(() => {
            const discount = solvedPaymentData.value?.price?.promocodeData?.promocodeDiscount
            return typeof discount === 'number' ? billingStore.formatPrice(discount) : ''
        })
        const promoRowAmount = computed(() => {
            if (!formattedPromoDiscount.value) return ''
            return `-${formattedPromoDiscount.value}`
        })
        // Активные промокоды проекта действуют на тариф, на выбранные интеграции или на весь заказ, поэтому
        // какой-то из них может не дать скидки в этом счёте — показываем каждый: на что он и до какого числа.
        const marketplaceStore = useMarketplaceStore()
        const promocodeIntegrationNames = computed<Record<string, string | undefined>>(() => {
            const projectId = project.value?.id
            const integrations = (projectId && marketplaceStore.integrations[projectId]) || []
            return Object.fromEntries(integrations.map(integration => [integration.id, integration.name]))
        })
        // Пока счёт не рассчитан, «не действует» не говорим.
        const orderPromocodes = computed(() =>
            solvedPaymentData.value?.price ? orderPromocodeCodes(solvedPaymentData.value.price.promocodeData) : null
        )
        const promocodeMissesOrder = (promocode: string) =>
            orderPromocodes.value !== null && !orderPromocodes.value.includes(promocode)
        const activePromocodeTexts = computed(() =>
            billingStore.projectPromocodes.map(promocode => ({
                key: promocode.promocode,
                ...describeActivePromocode({
                    promocode,
                    integrationNames: promocodeIntegrationNames.value,
                    missesOrder: promocodeMissesOrder(promocode.promocode),
                }),
            }))
        )
        watch(
            () => billingStore.projectPromocodes,
            promocodes => {
                const projectId = project.value?.id
                const needsNames = promocodes.some(promocode => promocode.integrationIds?.length)
                if (!needsNames || !projectId || marketplaceStore.integrations[projectId]) return
                void marketplaceStore.fetchIntegrations(projectId)
            },
            { immediate: true }
        )
        const subscriptionPeriodDiscountLabel = computed(() => {
            const percent = Number(solvedPaymentData.value?.price?.subscriptionPeriodDiscountPercent)
            if (!Number.isFinite(percent) || percent <= 0) return ''
            return `Скидка за период(-${Number(percent.toFixed(2))}%)`
        })
        const subscriptionPeriodDiscountRowAmount = computed(() => {
            const value = Number(solvedPaymentData.value?.price?.subscriptionPeriodDiscount)
            if (!Number.isFinite(value) || value <= 0) return ''
            return `-${billingStore.formatPrice(value)}`
        })
        const formattedResultPrice = computed(() => {
            const value = solvedPaymentData.value?.price?.toPay
            return typeof value === 'number' ? billingStore.formatPrice(value) : '0 ₽'
        })
        const formattedTotalRowPrice = computed(() => {
            const price = solvedPaymentData.value?.price
            const value = Number(
                (requiresScheduledTariffChange.value ? price?.needToPay : price?.toPay) || 0
            )
            return Number.isFinite(value) ? billingStore.formatPrice(value) : '0 ₽'
        })
        const formattedBalanceOfAccount = computed(() => {
            const value = solvedPaymentData.value?.price?.balanceOfAccount
            return typeof value === 'number' && value > 0 ? billingStore.formatPrice(value) : ''
        })
        const formattedUnusedDiscountRowAmount = computed(() => {
            const value = solvedPaymentData.value?.price?.unusedDiscount
            if (typeof value === 'number' && value > 0) {
                return `-${billingStore.formatPrice(value)}`
            }
            return ''
        })
        const hasDiscountAdjustments = computed(() => {
            const price = solvedPaymentData.value?.price
            if (!price) return false
            const hasPromoDiscount =
                Boolean(price?.promocodeData?.promocode) ||
                Number(price?.promocodeData?.promocodeDiscount || 0) > 0
            const hasSubscriptionPeriodDiscount = Number(price?.subscriptionPeriodDiscount || 0) > 0
            const hasUnusedDiscount = Number(price?.unusedDiscount || 0) > 0
            const hasBalanceDiscount = Number(price?.balanceOfAccount || 0) > 0
            return (
                hasPromoDiscount || hasSubscriptionPeriodDiscount || hasUnusedDiscount || hasBalanceDiscount
            )
        })
        const refundDescription = computed(() => {
            const value = solvedPaymentData.value?.price?.toRefund
            if (typeof value === 'number' && value > 0) {
                return `${billingStore.formatPrice(value)} останется на балансе после оплаты`
            }
            return ''
        })
        const formattedTariffRowAmount = computed(() => {
            const value = solvedPaymentData.value?.price?.toPayWithountDiscount
            return typeof value === 'number' ? billingStore.formatPrice(value) : '0 ₽'
        })
        const getPaymentLineItemAmount = (lineItem: any) =>
            typeof lineItem?.amount === 'number' ? lineItem.amount : 0
        const formatPaymentLineItemAmount = (lineItem: any) =>
            billingStore.formatPrice(getPaymentLineItemAmount(lineItem))
        const formattedTariffTableRowAmount = computed(() => {
            const lineItems = solvedPaymentData.value?.price?.lineItems
            if (Array.isArray(lineItems)) {
                const tariffLineItem = lineItems.find(
                    (lineItem: any) => lineItem?.kind === 'tariff' || lineItem?.isTariff === true
                )
                if (tariffLineItem) {
                    return formatPaymentLineItemAmount(tariffLineItem)
                }
            }
            return formattedTariffRowAmount.value
        })
        const formattedAddChannelTableRowAmount = computed(() => {
            const lineItems = solvedPaymentData.value?.price?.lineItems
            if (Array.isArray(lineItems)) {
                const integrationId = solvedPaymentData.value?.integrationId
                const lineItem =
                    (integrationId
                        ? lineItems.find((item: any) => item?.integrationId === integrationId)
                        : null) ||
                    lineItems.find(
                        (item: any) =>
                            !isServiceLineItem(item) &&
                            (item?.kind === 'slot' || !(item?.kind === 'tariff' || item?.isTariff === true))
                    )
                if (lineItem) {
                    return formatPaymentLineItemAmount(lineItem)
                }
            }
            return formattedTariffRowAmount.value
        })
        const servicePaymentLineItems = computed(() => {
            const lineItems = solvedPaymentData.value?.price?.lineItems
            if (!Array.isArray(lineItems)) return []
            return lineItems.filter(isServiceLineItem).map((lineItem: any, index: number) => ({
                key: `${lineItem?.integrationId || 'service'}-${index}`,
                name: String(lineItem?.name || 'Услуга'),
                periodRange: formatLineItemPeriodRange(lineItem) || '—',
                formattedAmount: formatPaymentLineItemAmount(lineItem),
            }))
        })
        const normalizeChannelDisplayName = (name?: string) => {
            if (!name) return 'Свободный канал'
            const normalized = String(name)
                .trim()
                .toLowerCase()
            const channelNameMap: Record<string, string> = {
                telegram: 'Telegram',
                telegramchannel: 'Telegram',
                whatsapp: 'WhatsApp',
                whatsappchannel: 'WhatsApp',
                max: 'Max',
                maxchannel: 'Max',
            }
            return channelNameMap[normalized] || name
        }
        const includedTariffChannelItems = computed(() => {
            const lineItems = solvedPaymentData.value?.price?.lineItems
            const liveLines = Array.isArray(lineItems)
                ? lineItems
                    .map((lineItem: any, index: number) => ({ lineItem, index }))
                    .filter(({ lineItem }) => !(lineItem?.kind === 'tariff' || lineItem?.isTariff === true))
                    .map(({ lineItem, index }) => {
                        const rawAmount = getPaymentLineItemAmount(lineItem)
                        return {
                            integrationId: String(lineItem?.integrationId || ''),
                            subscriptionId:
                                typeof lineItem?.subscriptionId === 'number' ? lineItem.subscriptionId : null,
                            name: String(lineItem?.name || ''),
                            displayName: normalizeChannelDisplayName(lineItem?.name),
                            subtitle: getChannelSubtitle(
                                String(lineItem?.integrationId || ''),
                                Boolean(lineItem?.inTariff),
                                lineItem?.name,
                                lineItem?.kind
                            ),
                            periodRange: isServiceLineItem(lineItem)
                                ? formatLineItemPeriodRange(lineItem) || '—'
                                : formatLineItemPeriodRange(lineItem),
                            formattedAmount: billingStore.formatPrice(rawAmount),
                            included: true,
                            order: index,
                        }
                    })
                : []
            const liveIds = new Set(
                liveLines
                    .map(item => item.subscriptionId)
                    .filter((id): id is number => typeof id === 'number')
            )
            const remembered = excludedAddonLines.value
                .filter(item => !liveIds.has(item.subscriptionId))
                .map(item => ({
                    integrationId: '',
                    subscriptionId: item.subscriptionId,
                    name: item.name,
                    displayName: item.name,
                    subtitle: item.meta || (item.isModule ? 'Модуль' : 'Дополнительный канал'),
                    periodRange: item.periodRange || item.period || '',
                    formattedAmount: formatExcludedAmount(item.amount),
                    included: false,
                    order: item.order,
                }))
            return [...liveLines, ...remembered].sort((left, right) => (
                Number(left.order) - Number(right.order)
            ))
        })
        const resolvePlanItemIcon = (lineItem: any) => {
            const normalizedName = String(lineItem?.name || '').toLowerCase()
            const integrationId = String(lineItem?.integrationId || '')
            if (normalizedName.includes('whatsapp')) {
                return {
                    iconClass: 'plan-row__icon--wa',
                    iconSrc: whatsappChannelIconUrl,
                }
            }
            if (normalizedName.includes('telegram')) {
                return {
                    iconClass: 'plan-row__icon--tg',
                    iconSrc: telegramChannelIconUrl,
                }
            }
            if (
                !isUnassignedChannelSlotName(lineItem?.name) &&
                (normalizedName.includes('sms') ||
                    integrationId === String(moduleIds.sms) ||
                    String(lineItem?.kind || '').toLowerCase() === 'sms')
            ) {
                return {
                    iconClass: 'plan-row__icon--sms',
                    iconSrc: smsChannelIconUrl,
                }
            }
            if (
                isMaxBusinessChannel({
                    name: lineItem?.name,
                    kind: lineItem?.kind,
                    integrationId: lineItem?.integrationId,
                })
            ) {
                return {
                    iconClass: 'plan-row__icon--max',
                    iconSrc: maxChannelIconUrl,
                }
            }
            return {
                iconClass: 'plan-row__icon--tariff',
                iconSrc: tariffIconUrl,
            }
        }
        const planSelectionItems = computed(() => {
            const lineItems = solvedPaymentData.value?.price?.lineItems
            if (!Array.isArray(lineItems) || !lineItems.length) {
                return [
                    {
                        integrationId: String(billingStore.selectedTariffId || 'tariff'),
                        subscriptionId: null,
                        name: tariffDisplayName.value,
                        displayName: tariffDisplayName.value,
                        subtitle: 'Тариф',
                        checkboxDisabled: true,
                        included: true,
                        lineKey: 'tariff',
                        slotId: '',
                        amount: 0,
                        period: '',
                        periodRange: '',
                        order: 0,
                        isModule: false,
                        inTariff: false,
                        ...resolvePlanItemIcon({ kind: 'tariff' }),
                    },
                ]
            }
            const liveLines = lineItems.map((lineItem: any, index: number) => {
                const isTariff = lineItem?.kind === 'tariff' || lineItem?.isTariff === true
                const isService = isServiceLineItem(lineItem)
                const isInTariff = Boolean(lineItem?.inTariff)
                const isModule = !isTariff
                    && !isService
                    && String(lineItem?.kind || '').toLowerCase() !== 'slot'
                    && lineItem?.integrationId !== 'channelSlot'
                const name = String(lineItem?.name || '')
                const subtitle = isTariff
                    ? 'Тариф'
                    : isService
                        ? 'Услуга'
                    : isModule
                        ? 'Модуль'
                        : getChannelSubtitle(
                              String(lineItem?.integrationId || ''),
                              isInTariff,
                              lineItem?.name,
                              lineItem?.kind
                          )
                const slotId = String(lineItem?.slotId || '')
                const periodRange = formatLineItemPeriodRange(lineItem)
                const period = periodRange || String(lineItem?.period || '')
                return {
                    integrationId: String(lineItem?.integrationId || ''),
                    subscriptionId:
                        typeof lineItem?.subscriptionId === 'number' ? lineItem.subscriptionId : null,
                    name,
                    displayName: isTariff
                        ? lineItem?.name || tariffDisplayName.value
                        : normalizeChannelDisplayName(lineItem?.name),
                    subtitle,
                    checkboxDisabled: isTariff || isInTariff || isService,
                    isModule,
                    inTariff: isInTariff,
                    included: true,
                    lineKey: `${slotId || name}|${subtitle}|${period}|${index}`,
                    slotId,
                    amount: lineItem?.amount ?? lineItem?.baseAmount ?? 0,
                    period,
                    periodRange,
                    order: index,
                    ...resolvePlanItemIcon(lineItem),
                }
            })
            const liveIds = new Set(
                liveLines
                    .map(line => line.subscriptionId)
                    .filter((id): id is number => typeof id === 'number')
            )
            const remembered = excludedAddonLines.value
                .filter(item => !liveIds.has(item.subscriptionId))
                .map(item => ({
                    integrationId: '',
                    subscriptionId: item.subscriptionId,
                    name: item.name,
                    displayName: item.name,
                    subtitle: item.meta || (item.isModule ? 'Модуль' : 'Дополнительный канал'),
                    checkboxDisabled: false,
                    isModule: Boolean(item.isModule),
                    inTariff: false,
                    included: false,
                    lineKey: `excluded|${item.subscriptionId}`,
                    slotId: item.slotId || '',
                    amount: item.amount,
                    period: item.period || '',
                    periodRange: item.periodRange || item.period || '',
                    order: item.order,
                    ...resolvePlanItemIcon({
                        name: item.name,
                        kind: item.isModule ? 'module' : 'slot',
                    }),
                }))
            return [...liveLines, ...remembered].sort((left, right) => (
                Number(left.order) - Number(right.order)
            ))
        })

        let lockedScrollY = 0
        let bodyScrollLocked = false

        const syncBodyScrollLock = (lock: boolean) => {
            if (typeof document === 'undefined') return
            const { body } = document
            if (lock) {
                if (bodyScrollLocked) return
                lockedScrollY = window.scrollY || window.pageYOffset || 0
                body.style.position = 'fixed'
                body.style.top = `-${lockedScrollY}px`
                body.style.left = '0'
                body.style.right = '0'
                body.style.width = '100%'
                body.style.overflow = 'hidden'
                bodyScrollLocked = true
                return
            }
            if (!bodyScrollLocked) return
            body.style.position = ''
            body.style.top = ''
            body.style.left = ''
            body.style.right = ''
            body.style.width = ''
            body.style.overflow = ''
            bodyScrollLocked = false
            window.scrollTo(0, lockedScrollY)
        }

        onBeforeUnmount(() => {
            syncBodyScrollLock(false)
        })

        watch(modalOpened, async opened => {
            syncBodyScrollLock(opened && shouldRenderSidebar.value)
            if (!opened || !shouldRenderSidebar.value) {
                resetShorterPeriodReveal()
                closeDowngradeModal()
                resetAddonExclusions()
                addOpz2KpiSettings.value = false
                addZvonoBotSettings.value = false
                closeSettingsRequest()
                return
            }
            showPromoForm.value = false
            promoCode.value = ''
            promoActivated.value = false
            promoInputShake.value = false
            promoStatusType.value = ''
            promoStatusMessage.value = ''
            promoLastSubmittedCode.value = ''
            promoActivationInProgress.value = false
            closeDetachChannelModal()
            closeDowngradeModal()
            resetAddonExclusions()
            addOpz2KpiSettings.value = false
            addZvonoBotSettings.value = false
            closeSettingsRequest()
            setDefaultPeriod()
            revealShorterPeriods.value = isSelectedPeriodShorterThanCurrent()
            if (props.mode === 'add-channel') {
                addChannelOption.value =
                    addChannelContext.value?.renewalMode === 'standalone' || isBeautyAutopilotStandaloneOnly.value
                        ? 'monthly'
                        : 'bundle'
            }
            if (project.value?.id) {
                // Витрина тарифов несёт и активный промокод проекта — обновляем при каждом открытии.
                // Ждём ответа, только если без тарифов не построить выбор тарифа.
                const tariffsRequest = billingStore.fetchTariffs(project.value.id)
                if (showTariffPicker.value && !billingStore.tariffs.length) await tariffsRequest
            }
            const needsSettingsPrice =
                ((showOpz2KpiAddon.value || isOpz2KpiSettingsPurchase.value) && !kpiSettingsIntegration.value) ||
                ((showZvonoBotSettingsAddon.value || isZvonoBotSettingsPurchase.value) &&
                    !zvonoBotSettingsIntegration.value)
            if (opened && needsSettingsPrice && project.value?.id) {
                await useMarketplaceStore().fetchIntegrations(project.value.id)
            }
            await loadSolvePayment()
            await fetchContractors()
        })

        const autoRenew = ref(false)
        const showPromoForm = ref(false)
        const promoCode = ref('')
        const promoActivated = ref(false)
        const promoInputShake = ref(false)
        const promoStatusType = ref<'success' | 'error' | ''>('')
        const promoStatusMessage = ref('')
        const promoLastSubmittedCode = ref('')
        const promoActivationInProgress = ref(false)
        const normalizedPromoCode = computed(() => promoCode.value.trim())
        const canActivatePromocode = computed(
            () =>
                Boolean(normalizedPromoCode.value) &&
                (!promoActivationInProgress.value ||
                    normalizedPromoCode.value !== promoLastSubmittedCode.value)
        )
        const startPromoInputShake = () => {
            promoInputShake.value = false
            setTimeout(() => {
                promoInputShake.value = true
                setTimeout(() => {
                    promoInputShake.value = false
                }, 320)
            }, 0)
        }
        const activatePromocode = async () => {
            if (!canActivatePromocode.value || !project.value?.id) return
            promoActivationInProgress.value = true
            promoLastSubmittedCode.value = normalizedPromoCode.value
            const { data, error } = await api.marketplace.activatePromocode({
                projectId: project.value.id,
                promocode: normalizedPromoCode.value,
            } as any)
            if (error) {
                promoStatusType.value = 'error'
                promoStatusMessage.value = 'Промокод не найден'
                promoCode.value = ''
                startPromoInputShake()
                promoActivationInProgress.value = false
                return
            }
            let success = false
            switch (data?.message) {
                case 'Promocode not found':
                    promoStatusType.value = 'error'
                    promoStatusMessage.value = 'Промокод не найден'
                    break
                case 'Promocode has expired':
                    promoStatusType.value = 'error'
                    promoStatusMessage.value = 'Срок действия промокода истек'
                    break
                case 'Promocode usage limit reached':
                    promoStatusType.value = 'error'
                    promoStatusMessage.value = 'Промокод уже использован'
                    break
                case 'Promocode already used for this project':
                    promoStatusType.value = 'error'
                    promoStatusMessage.value = 'Промокод уже использован'
                    break
                case 'Failed to activate promocode':
                    promoStatusType.value = 'error'
                    promoStatusMessage.value = 'Промокод не найден'
                    break
                case 'Promocode successfully activated':
                    promoStatusType.value = 'success'
                    promoStatusMessage.value = 'Промокод активирован'
                    promoActivated.value = true
                    success = true
                    await billingStore.fetchTariffs(project.value.id)
                    break
                default:
                    promoStatusType.value = 'error'
                    promoStatusMessage.value = 'Промокод не найден'
            }
            // После любого успешного ответа по промокоду пересчитываем счет.
            await loadSolvePayment()
            if (success) {
                promoStatusMessage.value = describeActivation({
                    replacedPromocodes: Array.isArray(data?.replacedPromocodes) ? data.replacedPromocodes : [],
                    missesOrder: promocodeMissesOrder(promoLastSubmittedCode.value),
                })
            }
            if (!success) {
                promoCode.value = ''
                startPromoInputShake()
            }
            promoActivationInProgress.value = false
        }
        watch(normalizedPromoCode, code => {
            if (promoActivationInProgress.value && code !== promoLastSubmittedCode.value) {
                promoActivationInProgress.value = false
            }
        })
        const action = ref<'pay' | 'choose'>('pay')

        const SIDEBAR_TRANSITION_MS = 250
        const onSidebarLeave = (_el: Element, done: () => void) => {
            setTimeout(done, SIDEBAR_TRANSITION_MS)
        }

        return {
            billingStore,
            modalOpened,
            shouldRenderSidebar,
            onSidebarLeave,
            closeModal,
            headerTitle,
            addChannelHeaderTitle,
            planSectionLabel,
            showTariffPicker,
            tariffPickerOptions,
            selectCheckoutTariff,
            tariffDisplayName,
            currentChannelName,
            currentChannelSubtitle,
            addChannelBillingSectionTitle,
            addChannelBundleHint,
            addChannelMonthlyHint,
            currentChannelIconSrc,
            currentChannelIconClass,
            tariffDisplaySubtitle,
            headerPeriodLabel,
            tablePeriodRange,
            addChannelTariffWarningText,
            isZeroDayPeriodRange,
            tariffIconUrl,
            periodCheckIcon,
            selectedPeriod,
            isPeriodSelected,
            addChannelOption,
            selectAddChannelOption,
            isAddChannelOptionSelected,
            selectPeriod,
            getPeriodDiscountLabel,
            subscriptionPeriodPills,
            showRevealShorterPeriodsButton,
            revealShorterStaged,
            isRevealingShorterPeriods,
            splitAnimActive,
            splitClones,
            setPeriodRowRef,
            setPeriodStageRef,
            loading,
            solvedPaymentData,
            promoName,
            promoRowAmount,
            activePromocodeTexts,
            subscriptionPeriodDiscountLabel,
            subscriptionPeriodDiscountRowAmount,
            hasDiscountAdjustments,
            formattedUnusedDiscountRowAmount,
            formattedTodayDate,
            formattedResultPrice,
            formattedTotalRowPrice,
            formattedBalanceOfAccount,
            refundDescription,
            formattedTariffRowAmount,
            formattedTariffTableRowAmount,
            formattedAddChannelTableRowAmount,
            isPaymentActionDisabled,
            requiresScheduledTariffChange,
            includedTariffChannelItems,
            planSelectionItems,
            shouldShowPaymentMethodSelection,
            isSidebarBusy,
            isInvoicePayment,
            paymentInfoWarningText,
            scheduledTariffChangeWarningText,
            isInvoice,
            selectedPayment,
            contractors,
            selectedContractor,
            selectPayment,
            deleteContractor,
            makePayment,
            handlePaymentButtonClick,
            handleInvoiceButtonClick,
            goToInvoice,
            createInvoiceWithExistingContractor,
            onPlanItemCheckboxClick,
            isDetachChannelModalOpened,
            detachChannelModalChannelName,
            detachChannelModalLoading,
            detachChannelModalIsModule,
            detachChannelModalInTariff,
            closeDetachChannelModal,
            handleDetachChannelCreatePackage,
            isDowngradeModalOpened,
            downgradeModalLoading,
            downgradeModalSuccess,
            downgradeModalPaymentUrl,
            needsDowngradeWizard,
            closeDowngradeModal,
            isScheduledBlockedModalOpened,
            closeScheduledBlockedModal,
            handleDowngradeConfirm,
            handleDowngradeSuccessClose,
            handleDowngradePayNow,
            showOpz2KpiAddon,
            isOpz2KpiSettingsPurchase,
            isBeautyAutopilotStandaloneOnly,
            addOpz2KpiSettings,
            kpiSettingsPriceLabel,
            showZvonoBotSettingsAddon,
            addZvonoBotSettings,
            zvonoBotSettingsPriceLabel,
            servicePaymentLineItems,
            activeSettingsRequest,
            closeSettingsRequest,
            continueAfterSettingsRequest,
            invoicePeriod,
            invoicePrice,
            invoicePaymentPayloadId,
            handleInvoiceBack,
            projectIdForInvoice,
            projNameForInvoice,
            promoActivated,
            promoInputShake,
            promoStatusType,
            promoStatusMessage,
            canActivatePromocode,
            activatePromocode,
            autoRenew,
            showPromoForm,
            promoCode,
            action,
        }
    },
})
